import { CommerceError } from './shopify.types';

export interface ShopifyConfig {
  domain: string;
  token: string;
  apiVersion: string;
}

export function readShopifyConfig(): ShopifyConfig | null {
  // Interrupteur explicite : les tests doivent rester déterministes même si un
  // .env local configure le vrai Storefront.
  if (import.meta.env.VITE_USE_FIXTURES === '1') return null;

  const domain = import.meta.env.VITE_SHOPIFY_STORE_DOMAIN?.trim();
  const token = import.meta.env.VITE_SHOPIFY_STOREFRONT_TOKEN?.trim();
  const apiVersion = import.meta.env.VITE_SHOPIFY_API_VERSION?.trim() || '2025-01';
  if (!domain || !token) return null;
  return { domain, token, apiVersion };
}

export function isShopifyConfigured(): boolean {
  return readShopifyConfig() !== null;
}

export function storefrontEndpoint(config: ShopifyConfig): string {
  return `https://${config.domain}/api/${config.apiVersion}/graphql.json`;
}

interface GraphQLResponse<T> {
  data?: T;
  errors?: { message: string }[];
}

/**
 * Appel Storefront. Toute réponse anormale devient une CommerceError lisible :
 * l'interface doit pouvoir proposer une sortie vers la boutique classique.
 */
export async function storefrontFetch<T>(
  config: ShopifyConfig,
  query: string,
  variables: Record<string, unknown> = {},
  signal?: AbortSignal,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(storefrontEndpoint(config), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.token,
      },
      body: JSON.stringify({ query, variables }),
      ...(signal ? { signal } : {}),
    });
  } catch {
    throw new CommerceError('La boutique ne répond pas. Vérifiez votre connexion.');
  }

  if (!response.ok) {
    throw new CommerceError(`Le catalogue a répondu ${response.status}.`);
  }

  let payload: GraphQLResponse<T>;
  try {
    payload = (await response.json()) as GraphQLResponse<T>;
  } catch {
    throw new CommerceError('Réponse illisible du catalogue.');
  }

  if (payload.errors?.length) {
    throw new CommerceError(payload.errors[0]?.message ?? 'Erreur du catalogue.');
  }
  if (!payload.data) {
    throw new CommerceError('Le catalogue n’a renvoyé aucune donnée.');
  }
  return payload.data;
}
